// The business vocabularies of CandyVoice: they write the acronyms and the
// terms of an organization right in the transcription of the voice translation
// and keep them from being translated. The common ones of CandyVoice, and the
// ones the owners and admins of an organization make in the Vocabularies tab
// of the organization page (Vocabularies.tsx). A meeting uses 5 of them at
// most, the default ones of the organization or the ones its organizer chose
// (RoomVocabularies.tsx, VocabulariesOptions.tsx). See
// Meetingrooms.VoiceTranslation.Vocabularies in the backend.

export const MAX_VOCABULARIES_PER_MEETING = 5;

// A vocabulary the organization can use
export interface VocabularySummary {
  name: string;
  language: string | null;
  // the number of terms
  terms: number;
  // one of CandyVoice, not of the organization: it can't be changed
  common: boolean;
  // used in the meetings when the organizer didn't choose
  default: boolean;
}

export interface VocabularyTerm {
  // the exact form to write in the transcription
  term: string;
  // added in brackets the first time the term is said
  definition?: string;
  // what the speech recognition writes instead of the term
  aliases?: string[];
  // other fields (category...) are kept as they are
  [key: string]: unknown;
}

export interface Vocabulary {
  name: string;
  // the language of the definitions
  language: string | null;
  terms: VocabularyTerm[];
}

// 1 to 64 characters among a-z 0-9 - _, starting with a letter or a digit
export const isValidVocabularyName = (name: string) =>
  /^[a-z0-9][a-z0-9_-]{0,63}$/.test(name);

// What went wrong, with what CandyVoice said about it
export interface VocabularyError {
  error: "invalid" | "conflict" | "not_found" | "too_big" | "failed";
  detail?: string | null;
}

const url = (organizationId: string, name?: string) =>
  `/rapi/organizations/${organizationId}/vocabularies` +
  (name ? `/${encodeURIComponent(name)}` : "");

const request = async (input: string, init?: RequestInit) => {
  let response;
  try {
    response = await fetch(input, {
      ...init,
      headers: { "Content-Type": "application/json" },
    });
  } catch (e) {
    const error: VocabularyError = { error: "failed" };
    throw error;
  }
  if (!response.ok) {
    let error: VocabularyError = { error: "failed" };
    try {
      error = await response.json();
    } catch (e) {
      if (response.status === 404) error = { error: "not_found" };
    }
    throw error;
  }
  return response.status === 204 ? null : response.json();
};

export const listVocabularies = async (
  organizationId: string
): Promise<VocabularySummary[]> => (await request(url(organizationId))).data;

export const getVocabulary = async (
  organizationId: string,
  name: string
): Promise<Vocabulary> => (await request(url(organizationId, name))).data;

// Create or replace it, with all its terms
export const putVocabulary = (organizationId: string, vocabulary: Vocabulary) =>
  request(url(organizationId, vocabulary.name), {
    method: "PUT",
    body: JSON.stringify({
      language: vocabulary.language,
      terms: vocabulary.terms,
    }),
  });

export const deleteVocabulary = (organizationId: string, name: string) =>
  request(url(organizationId, name), { method: "DELETE" });

export const setDefaultVocabularies = (
  organizationId: string,
  names: string[]
) =>
  request(`/rapi/organizations/${organizationId}/default_vocabularies`, {
    method: "PUT",
    body: JSON.stringify({ names }),
  });
